import { getEnvValue } from '../../../utils/env';

/**
 * AI Provider Configuration
 * Centralized configuration for all AI providers
 */

/**
 * Default AI Configuration
 */
export const DEFAULT_AI_CONFIG = {
  // Primary provider for CV extraction
  primaryProvider: process.env.REACT_APP_AI_PRIMARY_PROVIDER || 'openrouter',

  // Fallback providers in order of preference (gemini first as reliable free fallback)
  fallbackProviders: (getEnvValue('REACT_APP_AI_FALLBACK_PROVIDERS', 'AI_FALLBACK_PROVIDERS') || 'gemini,openai,anthropic')
    .split(',')
    .map(p => p.trim())
    .filter(Boolean),

  // Provider-specific configurations
  providers: {
    gemini: {
      name: 'gemini',
      displayName: 'Google Gemini',
      apiKey: getEnvValue('REACT_APP_GEMINI_API_KEY', 'GEMINI_API_KEY'),
      model: getEnvValue('REACT_APP_GEMINI_MODEL', 'GEMINI_MODEL') || 'gemini-2.5-flash',
      // baseUrl is auto-constructed in GeminiProvider: {baseUrl}/models/{model}:generateContent
      baseUrl: getEnvValue('REACT_APP_GEMINI_BASE_URL', 'GEMINI_BASE_URL') || undefined,
      enabled: true,
      priority: 1,
      pricing: {
        inputCostPerMillion: 0.075,  // $0.075 per 1M input tokens
        outputCostPerMillion: 0.30,  // $0.30 per 1M output tokens
        currency: 'USD'
      },
      rateLimits: {
        requestsPerMinute: 15,
        tokensPerMinute: 4000000,
        requestsPerDay: 1500
      },
      capabilities: {
        cvExtraction: true,
        immigrationAnalysis: true,
        streaming: false,
        functionCalling: true
      }
    },

    openai: {
      name: 'openai',
      displayName: 'OpenAI',
      apiKey: getEnvValue('REACT_APP_OPENAI_API_KEY', 'OPENAI_API_KEY'),
      model: getEnvValue('REACT_APP_OPENAI_MODEL', 'OPENAI_MODEL') || 'gpt-4o-mini',
      baseUrl: getEnvValue('REACT_APP_OPENAI_BASE_URL', 'OPENAI_BASE_URL') || 'https://api.openai.com/v1',
      enabled: !!getEnvValue('REACT_APP_OPENAI_API_KEY', 'OPENAI_API_KEY'),
      priority: 2,
      pricing: {
        inputCostPerMillion: 0.15,   // gpt-4o-mini
        outputCostPerMillion: 0.60,
        currency: 'USD'
      },
      rateLimits: {
        requestsPerMinute: 500,
        tokensPerMinute: 200000,
        requestsPerDay: 10000
      },
      capabilities: {
        cvExtraction: true,
        immigrationAnalysis: true,
        streaming: true,
        functionCalling: true
      }
    },

    anthropic: {
      name: 'anthropic',
      displayName: 'Anthropic Claude',
      apiKey: getEnvValue('REACT_APP_ANTHROPIC_API_KEY', 'ANTHROPIC_API_KEY'),
      model: getEnvValue('REACT_APP_ANTHROPIC_MODEL', 'ANTHROPIC_MODEL') || 'claude-3-5-sonnet-20241022',
      baseUrl: getEnvValue('REACT_APP_ANTHROPIC_BASE_URL', 'ANTHROPIC_BASE_URL') || 'https://api.anthropic.com/v1',
      enabled: !!getEnvValue('REACT_APP_ANTHROPIC_API_KEY', 'ANTHROPIC_API_KEY'),
      priority: 3,
      pricing: {
        inputCostPerMillion: 3.00,   // Claude 3.5 Sonnet
        outputCostPerMillion: 15.00,
        currency: 'USD'
      },
      rateLimits: {
        requestsPerMinute: 50,
        tokensPerMinute: 40000,
        requestsPerDay: 1000
      },
      capabilities: {
        cvExtraction: true,
        immigrationAnalysis: true,
        streaming: true,
        functionCalling: false
      }
    },

    azure: {
      name: 'azure',
      displayName: 'Azure OpenAI',
      apiKey: getEnvValue('REACT_APP_AZURE_OPENAI_KEY', 'AZURE_OPENAI_KEY'),
      model: getEnvValue('REACT_APP_AZURE_MODEL_NAME', 'AZURE_MODEL_NAME', 'REACT_APP_AZURE_OPENAI_DEPLOYMENT', 'AZURE_OPENAI_DEPLOYMENT') || 'DeepSeek-V3.1',
      baseUrl: getEnvValue('REACT_APP_AZURE_OPENAI_ENDPOINT', 'AZURE_OPENAI_ENDPOINT'),
      apiVersion: getEnvValue('REACT_APP_AZURE_API_VERSION', 'AZURE_API_VERSION', 'REACT_APP_AZURE_OPENAI_API_VERSION', 'AZURE_OPENAI_API_VERSION') || '2024-08-01-preview',
      enabled: !!(getEnvValue('REACT_APP_AZURE_OPENAI_KEY', 'AZURE_OPENAI_KEY') && getEnvValue('REACT_APP_AZURE_OPENAI_ENDPOINT', 'AZURE_OPENAI_ENDPOINT')),
      priority: 4,
      pricing: {
        inputCostPerMillion: 2.50,   // gpt-4o
        outputCostPerMillion: 10.00,
        currency: 'USD'
      },
      rateLimits: {
        requestsPerMinute: 300,
        tokensPerMinute: 150000,
        requestsPerDay: 10000
      },
      capabilities: {
        cvExtraction: true,
        immigrationAnalysis: true,
        streaming: true,
        functionCalling: true
      }
    },

    deepseek: {
      name: 'deepseek',
      displayName: 'DeepSeek',
      apiKey: getEnvValue('REACT_APP_DEEPSEEK_API_KEY', 'DEEPSEEK_API_KEY'),
      model: getEnvValue('REACT_APP_DEEPSEEK_MODEL', 'DEEPSEEK_MODEL') || 'deepseek-chat',
      baseUrl: getEnvValue('REACT_APP_DEEPSEEK_BASE_URL', 'DEEPSEEK_BASE_URL') || 'https://api.deepseek.com/v1',
      enabled: !!getEnvValue('REACT_APP_DEEPSEEK_API_KEY', 'DEEPSEEK_API_KEY'),
      priority: 5,
      pricing: {
        inputCostPerMillion: 0.27,   // DeepSeek-V3
        outputCostPerMillion: 1.10,
        currency: 'USD'
      },
      rateLimits: {
        requestsPerMinute: 60,
        tokensPerMinute: 60000,
        requestsPerDay: 5000
      },
      capabilities: {
        cvExtraction: true,
        immigrationAnalysis: true,
        streaming: true,
        functionCalling: true
      }
    },

    openrouter: {
      name: 'openrouter',
      displayName: 'OpenRouter',
      apiKey: process.env.REACT_APP_OPENROUTER_API_KEY || getEnvValue('REACT_APP_PROVIDER_API_KEY', 'OPENROUTER_API_KEY', 'REACT_APP_OPENROUTER_API_KEY', 'OPENROUTER_API_KEY'),
      model: getEnvValue('REACT_APP_OPENROUTER_MODEL', 'OPENROUTER_MODEL') || 'xiaomi/mimo-v2-flash:free',
      baseUrl: getEnvValue('REACT_APP_OPENROUTER_BASE_URL', 'OPENROUTER_BASE_URL') || 'https://openrouter.ai/api/v1',
      enabled: !!getEnvValue('REACT_APP_PROVIDER_API_KEY', 'OPENROUTER_API_KEY', 'REACT_APP_OPENROUTER_API_KEY', 'OPENROUTER_API_KEY'),
      priority: 5,
      pricing: {
        inputCostPerMillion: 3.00,
        outputCostPerMillion: 15.00,
        currency: 'USD'
      },
      rateLimits: {
        requestsPerMinute: 100,
        tokensPerMinute: 1000000,
        requestsPerDay: 10000
      },
      capabilities: {
        cvExtraction: true,
        immigrationAnalysis: true,
        streaming: true,
        functionCalling: true
      }
    }
  },

  // Task-specific model configurations
  tasks: {
    cvExtraction: {
      provider: getEnvValue('REACT_APP_CV_EXTRACTION_PROVIDER') || 'openrouter',
      model: getEnvValue('REACT_APP_CV_EXTRACTION_MODEL'),
      temperature: 0.1,
      maxTokens: 2048,
      topP: 0.95,
      topK: 40
    },

    immigrationAnalysis: {
      provider: getEnvValue('REACT_APP_IMMIGRATION_ANALYSIS_PROVIDER') || 'openrouter',
      model: getEnvValue('REACT_APP_IMMIGRATION_ANALYSIS_MODEL'),
      temperature: 0.2,
      maxTokens: 4096,
      topP: 0.9,
      topK: 32
    }
  },

  // Global settings
  settings: {
    enableFallback: true,
    maxRetries: 3,
    retryDelay: 800,
    timeout: 30000,
    enableCaching: true,
    cacheTTL: 3600000, // 1 hour
    logUsage: true
  }
};

/**
 * Get provider configuration by name
 */
export const getProviderConfig = (providerName) => {
  return DEFAULT_AI_CONFIG.providers[providerName] || null;
};

/**
 * Get all enabled providers sorted by priority
 */
export const getEnabledProviders = () => {
  return Object.values(DEFAULT_AI_CONFIG.providers)
    .filter(provider => provider.enabled)
    .sort((a, b) => a.priority - b.priority);
};

/**
 * Get task-specific configuration
 */
export const getTaskConfig = (taskName) => {
  return DEFAULT_AI_CONFIG.tasks[taskName] || null;
};

export default DEFAULT_AI_CONFIG;
